import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Button, useModals} from 'letrus-ui';
import DownloadModal from 'components/DownloadModal';
import {useEffect, useState} from 'react';
import downloadLearningPath from 'utils/functions/downloadLearningPath';
import downloadWriteActivity, {
  DownloadStatus,
} from 'utils/functions/downloadWriteActivity';
import useTrackEvent from 'utils/hooks/useTrackEvent';
import {Activity} from 'APIs/letrusAPI/activities/types';
import styles from './DownloadButton.module.scss';

enum ModalMessages {
  default = 'Lembre-se de ficar online antes do prazo da atividade acabar para poder enviá-la',
  update = 'Estamos preparando uma atualização, aguarde um pouco para poder baixar a atividade',
}

type ButtonStatus = 'ready' | 'disabled' | 'success' | 'error' | 'loading';

interface OwnProps {
  activity: Activity;
}

export type DownloadButtonProps = OwnProps;

function DownloadButton({activity}: DownloadButtonProps): JSX.Element {
  const {trackEvent} = useTrackEvent();
  const [disableDownload, setDisableDownload] = useState(false);
  const [status, setStatus] = useState<ButtonStatus>(() => getInitialStatus());
  const {downloadModal} = useModals(['downloadModal']);

  useEffect(() => {
    if ('ServiceWorker' in navigator) {
      navigator.serviceWorker
        .getRegistration()
        .then((registration) => {
          if (registration?.installing || registration?.waiting) {
            setDisableDownload(true);
          }

          navigator.serviceWorker.addEventListener('controllerchange', () => {
            setDisableDownload(false);
          });
        })
        .catch((error) => {
          console.log('service worker not available', error);
        });
    }

    return () => {
      if ('ServiceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('controllerchange', () => {
          setDisableDownload(false);
        });
      }
    };
  }, []);

  function getInitialStatus() {
    const {status} = activity;
    const isOngoingActivity = status === 'opened' || status === 'in_progress';

    return isOngoingActivity ? 'ready' : 'disabled';
  }

  function handleModalClose() {
    downloadModal?.close();
  }

  async function handleDownload(activity: Activity) {
    setStatus('loading');

    let downloadResult: Partial<DownloadStatus> = {};
    const isTest = activity.activity_type === 'test';

    if (isTest) {
      downloadResult = await downloadWriteActivity({
        testId: activity.id,
        themeId: activity.theme_id,
        testCompositionId: activity.composition_id,
        usesWritePreparation: activity.uses_write_preparation,
      });
    } else {
      downloadResult = await downloadLearningPath(
        activity.id,
        activity.steps,
        activity.get_opinion,
      );
    }

    if (downloadResult.success) {
      setStatus('success');

      trackEvent('offline_app_activity_download', {
        test: {
          test_id: activity.id,
        },
        learningPathInstance: {
          learning_path_instance_id: activity?.learning_path_instance_id ?? 0,
          learning_path_status: activity?.status,
        },
      });
    } else {
      // TODO: Wait for design to implement download error message
    }
  }

  return (
    <>
      <Button
        type="button"
        data-testid="download-button"
        aria-label="baixar atividade"
        disabled={status !== 'ready'}
        variant="secondary"
        rounded
        onClick={() => downloadModal?.showModal()}
        className={`${styles.downloadButton} ${styles[status]}`}
      >
        {status === 'success' && (
          <FontAwesomeIcon icon={['fas', 'check']} size="lg" />
        )}
        {status === 'ready' && (
          <FontAwesomeIcon icon={['fas', 'arrow-down']} size="lg" />
        )}
        {status === 'loading' && (
          <FontAwesomeIcon icon={['fas', 'spinner']} size="1x" spin />
        )}
      </Button>

      <DownloadModal
        onClose={handleModalClose}
        loading={status === 'loading'}
        fullfilled={status === 'success'}
        disableDownload={disableDownload}
        onSubmit={() => handleDownload(activity)}
        message={disableDownload ? ModalMessages.update : ModalMessages.default}
      />
    </>
  );
}

export default DownloadButton;
